/*
  This file keeps the trail of grid cells the pointer has passed over. Each
  cell has opacity and a timer, and their data is passed to a shader for
  rendering. New cells take the first free slot; when every slot is in use
  the oldest cell is dropped to make room.
*/

import * as THREE from 'three'

const TRAIL_DECAY_PER_SECOND = 3.84

// Must match the length of the shader's uCellData array, which reads it from here
export const TRAIL_CELL_CAPACITY = 128

// Class representing individual cells in the grid system
class CellData {
  pos: THREE.Vector2  // The position of the cell in the grid
  opacity: number     // The opacity value of the cell
  timer: number       // A timer used to manage the cell's state

  constructor(pos: THREE.Vector2 = new THREE.Vector2(-1, -1), opacity: number = 0.0, timer: number = 0.0) {
    this.pos = pos
    this.opacity = opacity
    this.timer = timer
  }

  // Copies data from another cell into this one
  copy(data: CellData) {
    this.pos.copy(data.pos)
    this.opacity = data.opacity
    this.timer = data.timer
  }

  // Resets the cell data to its initial state
  reset() {
    this.pos.x = -1
    this.pos.y = -1
    this.opacity = 0.0
    this.timer = 0.0
  }
}

export class CellGridSystem {

  private shaderData: THREE.Vector3[] // Array used to store the cell data for the shader
  private data: CellData[]            // Main container for all cell data in the grid
  private gridSize: THREE.Vector2     // Dimensions of the grid

  constructor(gridSize: THREE.Vector2) {
    this.data = Array.from({ length: TRAIL_CELL_CAPACITY }, () => new CellData())
    this.shaderData = Array.from({ length: TRAIL_CELL_CAPACITY }, () => new THREE.Vector3(-1, -1, 0))
    this.gridSize = gridSize
  }

  // Returns the current shader data
  getShaderData() {
    return this.shaderData
  }

  // Change grid size
  setGridSize(size: THREE.Vector2) : void {
    this.gridSize = size;
  }

  // Light up the cell under the pointer
  addCell(uv: THREE.Vector2): void {
    const gridCoords = this.mouseToGrid(uv)
    const cell = new CellData(new THREE.Vector2(gridCoords.x, gridCoords.y), 1.0, 1.0)

    const activeCellCount = this.data.filter(v => v.timer > 0.0).length
    if (activeCellCount + 1 > TRAIL_CELL_CAPACITY) {
      // Remove the oldest cells to make room for the new cell
      const cellsToRemove = (activeCellCount + 1) - TRAIL_CELL_CAPACITY

      // Shift cells to the left
      for (let i = 0; i < TRAIL_CELL_CAPACITY - cellsToRemove; i++) {
        this.data[i].copy(this.data[i + cellsToRemove])
      }

      // Reset the removed cells
      for (let i = 1; i < cellsToRemove + 1; i++) {
        this.data[TRAIL_CELL_CAPACITY - i].reset()
      }
    }

    // If the cell already exists, remove it so it is re-added as the newest
    const index = this.data.findIndex(v => v.pos.x === cell.pos.x && v.pos.y === cell.pos.y)
    if (index !== -1) {
      for (let i = index; i < TRAIL_CELL_CAPACITY - 1; i++) {
        this.data[i].copy(this.data[i + 1])
      }
    }

    // Add the cell if we have space
    const firstEmptyIndex = this.data.findIndex(v => v.pos.x === -1 && v.pos.y === -1)
    if (firstEmptyIndex !== -1) {
      this.data[firstEmptyIndex].copy(cell)
    }
  }

  // Cells stay fully visible until their timer nearly runs out, then switch off
  tick(dt: number): void {
    for (let i = 0; i < TRAIL_CELL_CAPACITY; i++) {
      if (this.data[i].timer >= 0.5) {
        this.data[i].opacity = 1.0
      }
      else if (this.data[i].timer < 0.1 && this.data[i].timer > 0.0) {
        this.data[i].opacity = 0.0
      }

      if (this.data[i].timer < 0.0) {
        this.data[i].reset()
      }

      this.data[i].timer -= TRAIL_DECAY_PER_SECOND * dt
    }

    // Update shader data based on current cell state
    for (let i = 0; i < TRAIL_CELL_CAPACITY; i++) {
      const data = this.data[i]
      this.shaderData[i].set(data.pos.x, data.pos.y, data.opacity)
    }
  }

  // Converts mouse coordinates to corresponding grid cell coordinates
  mouseToGrid(mouse: THREE.Vector2): { x: number; y: number } {
    // Calculate grid cell size
    const cellSizeX = 1 / this.gridSize.x // Size of each grid cell in x direction
    const cellSizeY = 1 / this.gridSize.y // Size of each grid cell in y direction

    const gridX = Math.floor(mouse.x / cellSizeX)
    const gridY = Math.floor(mouse.y / cellSizeY)

    return { x: gridX, y: gridY }
  }

}
